import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { Subscription } from 'rxjs';
import {
  IUpdateService,
  UpdateInfo,
  UpdateDownloadProgress,
  UPDATE_SERVICE,
} from '@infinitform/platform-core';

type BannerState = 'available' | 'downloading' | 'ready' | 'error';

@Component({
  selector: 'app-update-banner',
  templateUrl: './update-banner.component.html',
  styleUrls: ['./update-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class UpdateBannerComponent implements OnInit, OnDestroy {
  private readonly updateService = inject<IUpdateService>(UPDATE_SERVICE);
  private subscriptions: Subscription[] = [];

  readonly updateInfo = signal<UpdateInfo | null>(null);
  readonly state = signal<BannerState>('available');
  readonly downloadProgress = signal<UpdateDownloadProgress | null>(null);
  readonly errorMessage = signal<string>('');
  readonly showReleaseNotes = signal(false);

  readonly downloadSpeed = computed(() => {
    const progress = this.downloadProgress();
    if (!progress || progress.bytesPerSecond === 0) return '';
    const mbps = progress.bytesPerSecond / (1024 * 1024);
    return mbps >= 1 ? `${mbps.toFixed(1)} MB/s` : `${(mbps * 1024).toFixed(0)} KB/s`;
  });

  ngOnInit(): void {
    this.subscriptions.push(
      this.updateService.onUpdateAvailable().subscribe(info => {
        this.updateInfo.set(info);
        this.state.set('available');
      }),

      this.updateService.onDownloadProgress().subscribe(progress => {
        this.downloadProgress.set(progress);
      }),

      this.updateService.onDownloadComplete().subscribe(() => {
        this.state.set('ready');
        this.downloadProgress.set(null);
      }),

      this.updateService.onError().subscribe(err => {
        this.state.set('error');
        this.errorMessage.set(err.message);
        this.downloadProgress.set(null);
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  startDownload(): void {
    this.state.set('downloading');
    this.downloadProgress.set({ percent: 0, bytesPerSecond: 0, transferred: 0, total: 0 });
    this.subscriptions.push(this.updateService.downloadUpdate().subscribe());
  }

  cancelDownload(): void {
    this.updateService.cancelDownload();
    this.state.set('available');
    this.downloadProgress.set(null);
  }

  installUpdate(): void {
    this.updateService.installUpdate();
  }

  dismiss(): void {
    this.updateInfo.set(null);
    this.showReleaseNotes.set(false);
  }
}
